/**
 * Anonymous match-acceptance slot helpers shared by lobby match-ready UI
 * and Redux normalization of server contracts.
 */

export function buildAnonymousSlotsFromTotal(totalPlayers) {
  const count =
    Number.isInteger(totalPlayers) && totalPlayers > 0 ? totalPlayers : 2;
  const splitAt = Math.ceil(count / 2);
  const emptySlot = () => ({ accepted: false });
  return {
    team1: Array.from({ length: splitAt }, emptySlot),
    team2: Array.from({ length: Math.max(0, count - splitAt) }, emptySlot),
  };
}

export function normalizeAcceptanceSlots(slots, totalPlayers) {
  const team1 = Array.isArray(slots?.team1) ? slots.team1 : null;
  const team2 = Array.isArray(slots?.team2) ? slots.team2 : null;
  if (team1 && team2 && team1.length + team2.length > 0) {
    return {
      team1: team1.map((slot) => ({ accepted: Boolean(slot?.accepted) })),
      team2: team2.map((slot) => ({ accepted: Boolean(slot?.accepted) })),
    };
  }
  return buildAnonymousSlotsFromTotal(totalPlayers);
}

export function normalizePartyAcceptedUserIds(partyAcceptedUserIds) {
  if (!Array.isArray(partyAcceptedUserIds)) return [];
  return Array.from(
    new Set(
      partyAcceptedUserIds
        .map((userId) => (userId != null ? String(userId) : ""))
        .filter(Boolean)
    )
  );
}

/**
 * Personalized match-ready row: own party (lobby order) first with known
 * faces, then anonymous opponents. Accept flags for party mates come from
 * `partyAcceptedUserIds`; leftover accepts fill unknown slots left-to-right.
 */
export function buildViewerPlayerSlots({
  totalPlayers,
  partyMembers = [],
  selfUserId = null,
  partyAcceptedUserIds = [],
  acceptedCount = 0,
  hasAccepted = false,
} = {}) {
  const count =
    Number.isInteger(totalPlayers) && totalPlayers > 0 ? totalPlayers : 2;
  const acceptedParty = new Set(
    normalizePartyAcceptedUserIds(partyAcceptedUserIds)
  );
  if (hasAccepted && selfUserId) {
    acceptedParty.add(String(selfUserId));
  }

  const party = (Array.isArray(partyMembers) ? partyMembers : [])
    .filter((member) => member?.userId)
    .slice(0, count);

  const slots = [];
  let partyAccepted = 0;

  for (const member of party) {
    const userId = String(member.userId);
    const accepted = acceptedParty.has(userId);
    if (accepted) partyAccepted += 1;
    slots.push({
      kind: selfUserId && userId === String(selfUserId) ? "self" : "party",
      accepted,
      userId,
      member,
    });
  }

  let unknownAcceptedLeft =
    Math.max(0, Number(acceptedCount) || 0) - partyAccepted;
  const remaining = Math.max(0, count - slots.length);
  for (let i = 0; i < remaining; i += 1) {
    const accepted = unknownAcceptedLeft > 0;
    if (accepted) unknownAcceptedLeft -= 1;
    slots.push({
      kind: "unknown",
      accepted,
      userId: null,
      member: null,
    });
  }

  return slots;
}

export function remainingSecondsFromExpiresAt(expiresAt, nowMs = Date.now()) {
  const deadline = Number(expiresAt);
  if (!Number.isFinite(deadline)) return null;
  return Math.max(0, Math.ceil((deadline - nowMs) / 1000));
}

/** Same convention as the match page: Team_{lead nickname}. */
export function formatMatchTeamName(player) {
  const nick = String(
    player?.nickname || player?.name || player?.username || ""
  ).trim();
  return nick ? `Team_${nick}` : null;
}

export function formatAvgEloValue(avgElo) {
  const value = Number(avgElo);
  if (!Number.isFinite(value)) return null;
  return Math.round(value).toLocaleString("en-US");
}

function normalizeAcceptanceTeamSide(side) {
  if (!side || typeof side !== "object") {
    return { name: null, avgElo: null };
  }
  const name =
    typeof side.name === "string" && side.name.trim() ? side.name.trim() : null;
  const avgElo = Number.isFinite(Number(side.avgElo))
    ? Math.round(Number(side.avgElo))
    : null;
  return { name, avgElo };
}

export function normalizeAcceptanceTeams(teams) {
  return {
    yours: normalizeAcceptanceTeamSide(teams?.yours),
    theirs: normalizeAcceptanceTeamSide(teams?.theirs),
  };
}
